import { useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { MoreHorizontal, Copy, Trash2, Share2, Settings, Pencil } from 'lucide-react'
import { buildCogoLiveHostname, buildCogoLiveUrl, clearProjectGenerationFinalization, type ProjectMeta } from '@/services/project-api'
import type { AgentVillageStatus } from '@/features/agent/types'
import { AgentStatusBadge } from '@/features/agent/components/agent-village-card'
import { formatRelativeTime } from '@/features/agent/services/agent-village-service'
import { getProjectProofHealth } from '@/features/project/lib/project-proof-health'
import { formatStudioProjectPlanSummary } from '@/features/project/lib/studio-project-plan'
import { hasFailedGenerationFinalization, isOrphanedGenerationMarker } from '@/features/project/lib/project-generation-durability'
import { getGenerationFinalizationCopy } from '@/features/studio-dashboard/generation-finalization-copy'
import { getGenerationOrphanCopy } from '@/features/studio-dashboard/generation-orphan-copy'
import { useStudioLocale } from '@/i18n'
import { AppThumbnailPreview } from './app-thumbnail-preview'

// The status badge's words live in the locale dictionary
// (`projectCard.status`). They were an English record here, and it did not
// contain `active` — so a project in that state rendered its raw DATABASE
// value as a user-facing badge, in every language. The fallthrough below is
// kept for a status nobody has named yet; four are now named.

interface AppCardProps {
  project: ProjectMeta
  onOpen: (id: string) => void
  onDuplicate: (project: ProjectMeta) => void
  onDelete: (project: ProjectMeta) => void
  onRename?: (project: ProjectMeta) => void
  onSettings?: (project: ProjectMeta) => void
  onShare?: (project: ProjectMeta) => void
  agentStatus?: AgentVillageStatus
  onAgentTimeline?: () => void
}

export function AppCard({ project, onOpen, onDuplicate, onDelete, onRename, onSettings, onShare, agentStatus, onAgentTimeline }: AppCardProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const studioLocale = useStudioLocale()
  const { locale } = studioLocale

  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const cardCopy = studioLocale.strings.projectCard
  const liveHostname = project.slug ? buildCogoLiveHostname(project.slug) : null
  const liveUrl = project.slug ? buildCogoLiveUrl(project.slug) : null
  const planSummary = formatStudioProjectPlanSummary(
    project.studioProjectPlan,
    project.app_target,
    locale,
  )
  const proofHealth = getProjectProofHealth(studioLocale, project)
  const isLive = project.status === 'published' && proofHealth.status === 'verified'
  // Legacy `published` metadata only says that an older publish path recorded
  // a deployment. Reserve Live/Shared for the independently read-back release.
  const statusLabel = project.status === 'published' && !isLive
    ? proofHealth.label
    : cardCopy.status[project.status as keyof typeof cardCopy.status] ?? project.status
  // G1 — a generation still 'pending' past the 10-minute threshold is an
  // orphan (browser died between create and persist). Honest labeling: it
  // must never render as a normal draft.
  const generationOrphaned = isOrphanedGenerationMarker(project.generation, Date.now())
  const orphanCopy = getGenerationOrphanCopy(locale)
  // G1 follow-up — the workflow's durable write succeeded (blocks saved,
  // marker 'complete') but the browser-side generation flow failed before it
  // acknowledged that persistence (see `GenerationFinalizationFailure` for
  // the exact window — a post-acknowledgment redirect failure is NOT it).
  // This is NOT the orphan case (nothing is missing server-side) so it must
  // never read as "didn't finish". Two gates are both required, explicitly,
  // in code: (a) `state === 'complete'` — `hasFailedGenerationFinalization`
  // only inspects the nested `finalization` payload, so without this gate a
  // still-'pending' marker that happens to carry a `finalization.state:
  // 'failed'` payload would render the "everything is saved" card for a
  // project its own marker says is NOT saved yet; (b) `!generationOrphaned`
  // — orphaned wins when a marker somehow carries both signals, rather than
  // relying on the fact that today's two writers never produce that
  // combination together; a directly-constructed marker could.
  //
  // Retirement: the notice is a one-time nudge, not a permanent badge. The
  // marker's `finalization` is otherwise cleared only by a subsequent
  // successful generation into the same project, so a user who does exactly
  // what the card asks would keep seeing it on every dashboard visit
  // forever. Activating the CTA IS the acknowledgment (no second button):
  // it retires the notice locally for this render and fires the durable
  // clear best-effort, without blocking or failing the open.
  const [finalizationRetiredForId, setFinalizationRetiredForId] = useState<string | null>(null)
  const generationFinalizationFailed =
    !generationOrphaned &&
    finalizationRetiredForId !== project.id &&
    project.generation?.state === 'complete' &&
    hasFailedGenerationFinalization(project.generation)
  const finalizationCopy = getGenerationFinalizationCopy(locale)
  const openAndRetireFinalizationNotice = () => {
    setFinalizationRetiredForId(project.id)
    // Detached on purpose: navigation must never wait on (or be cancelled
    // by) this write. `clearProjectGenerationFinalization` already never
    // throws; the `.catch` is the belt-and-suspenders guarantee that no
    // future change to that contract can turn this into an unhandled
    // rejection on a click handler.
    void clearProjectGenerationFinalization(project.id).catch((cause) => {
      console.warn(
        '[AppCard] Failed to clear the generation finalization notice',
        { projectId: project.id, cause },
      )
    })
    onOpen(project.id)
  }

  return (
    <div
      data-testid="studio-project-card"
      className="group relative rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-muted/30"
    >
      <button
        data-testid="studio-project-card-open"
        onClick={() => onOpen(project.id)}
        className="w-full text-left"
      >
        {/* Thumbnail area */}
        <AppThumbnailPreview
          title={project.name}
          imageSrc={project.thumbnail}
          projectId={project.id}
          kind="project"
          className="mb-3"
        />

        <div className="flex items-start justify-between">
          {/* Reported defect: long names were cut at the card edge with no way
              to recover them — "Bank Branch Locator App. 지점명이나 주소로…"
              measured 404px of text inside a 193px box and carried no `title`,
              so more than half the name was simply unreadable. `min-w-0` keeps
              the ellipsis honest inside the flex row, and `title` (the same
              treatment as `template-card.tsx`) keeps the full name reachable
              rather than discarded. */}
          <h3
            data-testid="app-card-name"
            className="min-w-0 text-sm font-medium text-foreground truncate pr-8"
            title={project.name}
          >
            {project.name}
          </h3>
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-background font-medium shrink-0 flex items-center gap-1 text-muted-foreground',
            )}
          >
            <span className={cn('w-1.5 h-1.5 rounded-full', isLive ? 'bg-foreground' : 'bg-muted-foreground/40')} />
            {statusLabel}
          </span>
          {agentStatus && (
            <AgentStatusBadge status={agentStatus} onTimelineClick={onAgentTimeline} />
          )}
        </div>
        {project.description && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{project.description}</p>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          v{project.version} · {new Date(project.updated_at).toLocaleDateString('ko-KR')}
        </p>
        <p
          data-testid="app-card-project-plan"
          className="mt-1 text-[10px] font-medium text-muted-foreground truncate"
          title={planSummary}
        >
          {planSummary}
        </p>
        {!generationOrphaned && (
          <p
            data-testid="app-card-proof-health"
            className={cn('mt-1 truncate text-[10px] font-semibold', proofHealthTextClassName(proofHealth.tone))}
            title={proofHealth.detail}
          >
            {proofHealth.label} · {proofHealth.detail}
          </p>
        )}
        {agentStatus?.last_action && agentStatus.last_event_at && (
          <p className="mt-1 text-[10px] text-muted-foreground truncate">
            {formatRelativeTime(agentStatus.last_event_at)}: {agentStatus.last_action}
          </p>
        )}
        {isLive && liveHostname && liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-1 inline-flex min-h-10 max-w-full items-center text-[10px] text-foreground hover:underline"
            title={liveHostname}
          >
            <span className="truncate">{liveHostname}</span>
          </a>
        )}
      </button>

      {/* G1 orphan surface — outside the open button (no nested buttons). */}
      {generationOrphaned && (
        <div
          data-testid="app-card-generation-orphan"
          className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2 dark:border-amber-500/40 dark:bg-amber-950/30"
        >
          <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">
            {orphanCopy.label}
          </p>
          <p className="mt-0.5 text-[10px] leading-4 text-amber-700/80 dark:text-amber-400/80">
            {orphanCopy.detail}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              type="button"
              data-testid="app-card-generation-orphan-resume"
              onClick={(e) => { e.stopPropagation(); onOpen(project.id) }}
              className="rounded-md border border-amber-400/60 bg-background px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted"
            >
              {orphanCopy.resume}
            </button>
            <button
              type="button"
              data-testid="app-card-generation-orphan-delete"
              onClick={(e) => { e.stopPropagation(); onDelete(project) }}
              className="rounded-md px-2 py-1 text-[10px] font-semibold text-destructive hover:bg-accent"
            >
              {orphanCopy.deleteAction}
            </button>
          </div>
        </div>
      )}

      {/* G1 finalization-failure surface — outside the open button (no
          nested buttons). Distinct from the orphan surface: the generation
          DID finish and its blocks ARE saved; only the browser-side flow
          failed before acknowledging that, so the copy nudges the user to
          open and check rather than claiming anything is unfinished or
          lost. The single CTA both opens the project and retires the
          notice — following the instruction is the acknowledgment. */}
      {generationFinalizationFailed && (
        <div
          data-testid="app-card-generation-finalization"
          className="mt-2 rounded-lg border border-sky-300/60 bg-sky-50 p-2 dark:border-sky-500/40 dark:bg-sky-950/30"
        >
          <p className="text-[11px] font-semibold text-sky-700 dark:text-sky-400">
            {finalizationCopy.label}
          </p>
          <p className="mt-0.5 text-[10px] leading-4 text-sky-700/80 dark:text-sky-400/80">
            {finalizationCopy.detail}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              type="button"
              data-testid="app-card-generation-finalization-open"
              onClick={(e) => { e.stopPropagation(); openAndRetireFinalizationNotice() }}
              className="rounded-md border border-sky-400/60 bg-background px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted"
            >
              {finalizationCopy.openAction}
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div ref={menuRef} className="absolute top-3 right-3 flex items-center gap-0.5">
        <button
          type="button"
          aria-label={cardCopy.shareNamed(project.name)}
          onClick={(e) => { e.stopPropagation(); onShare?.(project) }}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-background/80 text-muted-foreground opacity-100 shadow-sm backdrop-blur-sm transition-all hover:bg-muted hover:text-foreground md:opacity-0 md:group-hover:opacity-100"
          title={cardCopy.share}
        >
          <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          type="button"
          aria-label={cardCopy.openActions(project.name)}
          aria-expanded={menuOpen}
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-background/80 text-muted-foreground opacity-100 shadow-sm backdrop-blur-sm transition-all hover:bg-muted hover:text-foreground md:opacity-0 md:group-hover:opacity-100"
        >
          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-36 rounded-xl border border-border bg-popover shadow-md py-1 z-10">
            {onRename && (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onRename(project) }}
                className="flex h-10 w-full items-center gap-2 px-3 text-sm text-foreground hover:bg-accent"
              >
                <Pencil className="w-3.5 h-3.5" /> {cardCopy.rename}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDuplicate(project) }}
              className="flex h-10 w-full items-center gap-2 px-3 text-sm text-foreground hover:bg-accent"
            >
              <Copy className="w-3.5 h-3.5" /> {cardCopy.duplicate}
            </button>
            {onSettings && (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSettings(project) }}
                className="flex h-10 w-full items-center gap-2 px-3 text-sm text-foreground hover:bg-accent"
              >
                <Settings className="w-3.5 h-3.5" /> {cardCopy.settings}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(project) }}
              className="flex h-10 w-full items-center gap-2 px-3 text-sm text-destructive hover:bg-accent"
            >
              <Trash2 className="w-3.5 h-3.5" /> {cardCopy.delete}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function proofHealthTextClassName(tone: ReturnType<typeof getProjectProofHealth>['tone']): string {
  if (tone === 'blocked') return 'text-destructive'
  return 'text-muted-foreground'
}
